import { Label, SubLabel } from '../StatusBar/common';
import { __ } from '../../utilities';
import { mb } from '../../Themes/utilities';
import { rem } from 'polished';
import { removeAccents } from '@sportnet/utilities';
import Icon from '../../Icon';
import Input from '../../Input';
import React from 'react';
import SportnetApi from '../../Authorization/SportnetApi';
import StatusBar from '../StatusBar';
import styled, { useTheme } from 'styled-components';
const Wrapper = styled.div `
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
  max-height: 100%;
  ${mb('s')} {
    max-height: ${rem(310)};
  }
`;
const Line = styled.div `
  flex-shrink: 0;
  height: ${rem(1)};
  width: 100%;
  background-color: ${({ theme }) => theme.separatorColor};
`;
const InputWrapper = styled.div `
  width: 100%;
  padding: ${rem(16)};
  flex-shrink: 0;
`;
const OrgProfileName = styled.div `
  margin-left: ${rem(16)};
  color: ${({ theme }) => theme.textColor};
  font-weight: 500;
  font-size: ${rem(16)};
  text-align: initial;
`;
const OrgName = styled.div `
  margin-top: ${rem(4)};
  font-size: ${rem(14)};
  font-weight: normal;
  color: ${({ theme }) => theme.color.fadedText};
`;
const List = styled.div `
  padding: ${rem(8)} 0;
  overflow-y: auto;
`;
const AppSpaceWrapper = styled.a `
  padding: ${rem(16)};
  display: flex;
  text-decoration: none;
  color: ${({ theme }) => theme.textColor};
  font-family: ${({ theme }) => theme.fontFamily};
  background-color: 'transparent';
  transition: background-color 0.1s ease;
  &:hover {
    background-color: rgba(0, 0, 0, 0.05);
  }
  &:focus {
    background-color: rgba(0, 0, 0, 0.1);
  }
  &:active {
    background-color: rgba(0, 0, 0, 0.2);
  }
`;
const LogoWrapper = styled.div `
  height: ${({ $size }) => rem($size)};
  max-width: ${({ $maxWidth }) => rem($maxWidth)};
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;
const LogoImg = styled.img `
  max-width: 100%;
  max-height: 100%;
`;
const ChevronIcon = styled(Icon) `
  margin-left: ${rem(8)};
  display: none;
  ${mb(550)} {
    display: initial;
  }
`;
const ResponsiveLabel = styled(Label) `
  display: none;
  ${mb('l')} {
    display: block;
    margin-left: ${rem(12)};
  }
`;
const NoAppSpaces = styled.div `
  padding: ${rem(16)};
  text-align: center;
  color: ${({ theme }) => theme.inactiveColor};
  font-size: ${rem(14)};
  font-weight: 500;
  font-family: ${({ theme }) => theme.fontFamily};
`;
const NoIcon = styled.div `
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  height: ${rem(40)};
  width: ${rem(40)};
  background-color: #f5f5f5;
`;
const StatusBarAppSpaceSwitch = ({ appSpace, appSpaces, app, className, admin, }) => {
    const inputRef = React.useRef(null);
    const debounceRef = React.useRef(null);
    const [searchQuery, setSearchQuery] = React.useState('');
    const [opened, setOpened] = React.useState(false);
    const [filteredAppSpaces, setFilteredAppSpaces] = React.useState([]);
    const [loading, setIsLoading] = React.useState(false);
    const theme = useTheme();
    const orgProfileName = appSpace.name || appSpace._id;
    const orgName = appSpace?.organization_name || '';
    React.useEffect(() => {
        setFilteredAppSpaces(appSpaces?.filter(a => !!a && a?._id !== appSpace?._id) || []);
    }, [appSpaces, appSpace]);
    React.useEffect(() => {
        return () => {
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }
        };
    }, []);
    React.useEffect(() => {
        if (opened && inputRef.current) {
            inputRef.current.focus();
        }
    }, [opened]);
    const handleClose = React.useCallback(() => {
        setOpened(false);
    }, []);
    let IconEl = null;
    if (appSpace && appSpace.logo_public_url) {
        IconEl = (React.createElement(LogoWrapper, { "$size": 24, "$maxWidth": 72 },
            React.createElement(LogoImg, { src: appSpace.logo_public_url, alt: orgProfileName })));
    }
    else {
        IconEl = React.createElement(Icon, { size: "l", name: "suitcase", color: theme.color.primary });
    }
    const handleSearchChange = (searchQuery) => {
        let result = (appSpaces || []).filter(a => !!a && a?._id !== appSpace?._id);
        if (searchQuery) {
            const searchQueryWithoutAccents = removeAccents(searchQuery);
            if (admin) {
                setIsLoading(true);
                SportnetApi.organizationPPOProfiles({
                    q: searchQueryWithoutAccents,
                })
                    .then(({ items }) => {
                    result = items?.map(e => ({
                        _id: e._id || '',
                        name: e.name || '',
                        organization_id: e.organization_id || '',
                        organization_name: e.organization_name || '',
                        ...e,
                    }));
                    setFilteredAppSpaces(result?.filter(a => !!a && a?._id !== appSpace?._id));
                })
                    .finally(() => {
                    setIsLoading(false);
                });
            }
            else {
                setFilteredAppSpaces(result.filter(e => {
                    const orgProfileId = e._id;
                    const profileName = e.organization_name;
                    const organizationName = e.name;
                    return (removeAccents(orgProfileId).includes(searchQueryWithoutAccents) ||
                        removeAccents(profileName).includes(searchQueryWithoutAccents) ||
                        removeAccents(organizationName).includes(searchQueryWithoutAccents));
                }));
            }
        }
        else {
            setFilteredAppSpaces(result);
        }
    };
    if (!appSpaces) {
        return (React.createElement(StatusBar.Item, null,
            IconEl,
            React.createElement(ResponsiveLabel, null,
                orgProfileName,
                React.createElement(SubLabel, null, orgName))));
    }
    const dropdownBody = (React.createElement(Wrapper, null,
        React.createElement(InputWrapper, null,
            React.createElement(Input, { placeholder: "Vyh\u013Ead\u00E1vanie", value: searchQuery, onChange: (e) => {
                    if (debounceRef.current) {
                        clearTimeout(debounceRef.current);
                    }
                    const currentQuery = e?.target?.value || '';
                    setSearchQuery(currentQuery);
                    debounceRef.current = setTimeout(() => {
                        handleSearchChange(currentQuery);
                    }, admin ? 400 : 0);
                }, ref: inputRef },
                React.createElement(Input.Icon, { name: "search", size: "l" }),
                React.createElement("input", null),
                loading && React.createElement(Input.Loading, null))),
        React.createElement(Line, null),
        React.createElement(List, null, !!filteredAppSpaces.length ? (filteredAppSpaces.map(a => {
            let url = '';
            if (app && app.appSpaceUrlPattern) {
                url = app.appSpaceUrlPattern.replace('{{appSpace}}', a._id);
            }
            const orgProfileNameLocal = a ? a.name || a._id : '';
            const orgNameLocal = a.organization_name || '';
            return (React.createElement(AppSpaceWrapper, { key: a._id, href: url },
                a.logo_public_url ? (React.createElement(LogoWrapper, { "$size": 40, "$maxWidth": 40 },
                    React.createElement(LogoImg, { src: a.logo_public_url, alt: orgProfileNameLocal }))) : (React.createElement(NoIcon, null,
                    React.createElement(Icon, { size: 24, name: "image", color: "#CECECE" }))),
                React.createElement(OrgProfileName, null,
                    orgProfileNameLocal,
                    React.createElement(OrgName, null, orgNameLocal))));
        })) : (React.createElement(NoAppSpaces, null, __('Žiadne výsledky'))))));
    return (React.createElement(StatusBar.DropDown, { className: className, position: "left", width: 424, body: dropdownBody, opened: opened, onClose: handleClose },
        React.createElement(StatusBar.Button, { active: opened, onClick: () => {
                setOpened(o => !o);
            } },
            IconEl,
            React.createElement(ResponsiveLabel, null,
                orgProfileName,
                React.createElement(SubLabel, null, orgName)),
            React.createElement(ChevronIcon, { name: "triangle-down", size: "m" }))));
};
export default React.memo(StatusBarAppSpaceSwitch);
